import { LoadingButton } from "@mui/lab";
import { Autocomplete, Box, Card, CardContent, Grid, TextField } from "@mui/material";
import { MDTypography } from "../../Component/Common/Components/MDTypography";
import { MDBox } from "../../Component/Common/Components/MDBox";
import { useEffect, useState } from "react";
import { VerificationAgencyService } from "../../Component/Api/VerificationAgency/VerificationAgencyService";
import { VerificationAgency } from "../../Component/Model/Dto/VerificationAgency/VerificationAgency";
import { ScorecardDropdownDto } from "../../Component/Model/Dto/ScorecardLicense";
import { BEEScorecardService } from "../../Component/Api/BEEScorecard/BEEScorecard";
import { toast } from "react-toastify";
import { IntermediateGlobalLoading } from "../../Component/Common/Components/Loading/IntermediateGlobalLoading";
import { SkeletonLoading } from "../../Component/Common/SkeletonLoading/SkeletonLoading";
import { VAExcelExportService } from "../../Component/Api/VAExcelExport/VAExcelExportService";
import { VAExcelExportsHistoryTable } from "./VAExcelExportsHistoryTable";
import AutocompleteWithInfo, { IDropDownOptionsWithDescription } from "../../Component/Common/Components/Autocomplete/AutocompleteWithInfo";
import { useForm } from "react-hook-form";
import { DateFormatter } from "../../Component/Model/helpers";
import { UserAtom } from "../../State/user.Atom";
import { useRecoilValue } from "recoil";
import { ProgressBar } from "react-bootstrap";
import { useSignalRConnection } from "../../Hooks/useSignalRConnection";

export const VAExcelExports = () => {
    const [verificationAgencies, setVerificationAgencies] = useState<VerificationAgency.ExportConfigVerificationAgencyWithConfigDto[]>([]);
    const [verificationAgency, setVerificationAgency] = useState<VerificationAgency.ExportConfigVerificationAgencyWithConfigDto | undefined>(undefined);
    const [scorecards, setScorecards] = useState<IDropDownOptionsWithDescription[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isButtonLoading, setIsButtonLoading] = useState(false);
    const [scorecardsLoading, setScorecardsLoading] = useState(false);
    const [tableRefreshKey, setTableRefreshKey] = useState(0);
    const [progress, setProgress] = useState(0);
    const [isProcessingFile, setIsProcessingFile] = useState(false);
    const [usesSignalR, setUsesSignalR] = useState<boolean>(false);
    const User = useRecoilValue(UserAtom);
    const { control, setValue, watch } = useForm({ mode: "all" });
    const selectedScorecard = watch("scorecard");
    const hub = useSignalRConnection("VaExcelExportsHub");

    const exportVAPack = async () => {
        if (!selectedScorecard || !verificationAgency) {
            toast.warn("Please select both a Verification Agency and a Scorecard.");
            return;
        }

        const isInProgress = await checkVAExportInProgress(selectedScorecard.id.toString());
        if (isInProgress) {
            toast.warn("An export is already in progress for this scorecard. Please wait for it to complete.");
            return;
        }

        setIsButtonLoading(true);
        try {
            setUsesSignalR(true);
            const vaPackArray = await fetchVAPackData(selectedScorecard.id.toString());
            if (!vaPackArray) { return; }

            const formData = buildExportFormData(selectedScorecard.id.toString(), verificationAgency, vaPackArray);

            const response = await VAExcelExportService.ExportVAPack(formData);
            if (response.data.success === false || response.status !== 200) {
                toast.error(response.data.errorMessage?.trim() ? response.data.errorMessage : "Failed to export VA pack");
                return;
            } else {
                setIsProcessingFile(true);
                toast.success("Export started");
            }
        } catch (error: any) {
            console.error(error);
            toast.error("An unexpected error occurred while creating the VA export pack");
        } finally {
            setIsButtonLoading(false);
            setTableRefreshKey(prev => prev + 1);
        }
    };

    const checkVAExportInProgress = async (scorecardId: string): Promise<boolean> => {
        try {
            const response = await VAExcelExportService.CheckVAExportInProgress(scorecardId);
            return response.data ?? false;
        } catch (error) {
            console.error("Failed to check export status:", error);
            return false;
        }
    };

    const fetchVAPackData = async (scorecardId: string) => {
        const vaPackResponse = await VAExcelExportService.GetVAPack(scorecardId);
        if (vaPackResponse.status !== 200) {
            toast.error("Failed to retrieve VA pack data");
            return null;
        }
        return vaPackResponse.data;
    };

    const buildExportFormData = (
        selectedScorecardId: string,
        verificationAgency: VerificationAgency.ExportConfigVerificationAgencyWithConfigDto,
        vaPackArray: any
    ) => {
        const formData = new FormData();
        formData.append("ScorecardId", selectedScorecardId ?? "");
        formData.append("VerificationAgencyId", verificationAgency.vaId);
        const vaPackFile = new Blob([vaPackArray], { type: "application/octet-stream" });
        formData.append("FileByteStream", vaPackFile, "export.bin");
        return formData;
    };

    const mapScorecardsToDropdownOptions = (scorecards: ScorecardDropdownDto[]): IDropDownOptionsWithDescription[] => {
        return scorecards.map((sc) => {
            const startDate = DateFormatter(sc.startDate.toString())
            const endDate = DateFormatter(sc.endDate.toString())
            const date = `${sc.scorecardType} | ${startDate} - ${endDate}`;

            return {
                id: sc.id,
                value: sc.scorecardName,
                description: date
            };
        });
    };

    const handleScorecardChange = (value: string): void => {
        const selected = scorecards.find(sc => sc.value === value || sc.id.toString() === value);
        setValue("scorecard", selected);
    }

    const getVerificationAgencies = async () => {
        setIsLoading(true);
        const mappedVerificationAgencies = await VerificationAgencyService.GetVerificationAgenciesWithConfig();
        setVerificationAgencies(mappedVerificationAgencies.data);
        setIsLoading(false);
    }

    const getScorecardsAvailableForVerification = async () => {
        setScorecardsLoading(true);
        const scorecardsRes = await BEEScorecardService.getScorecardsAvailableForVerificationExcelExport();
        setScorecards(mapScorecardsToDropdownOptions(scorecardsRes.data));
        setScorecardsLoading(false);
    };

    useEffect(() => {
        getVerificationAgencies();
        getScorecardsAvailableForVerification();
    }, []);

    useEffect(() => {
        if (!usesSignalR) return;
        (async () => {
            try {
                if (!hub.isConnected) {
                    await hub.start();
                }

                hub.on(`ExportProgress-${User.username}`, (data: number) => {
                    setProgress(data);
                });

                hub.on(`ExportResult-${User.username}`, (data: any) => { 
                    setIsProcessingFile(false);
                    setProgress(0);
                    if (data && data?.success === true) {
                        toast.success(data?.message ?? "Export completed successfully");
                    } else {
                        toast.error(data?.message ?? "Export failed");
                    }
                    setTableRefreshKey(prev => prev + 1);
                });
            }
            catch (error) {
                console.error("SignalR connection failed: ", error);
            }
        })();
    }, [usesSignalR]);

    const RenderDropdowns = () => (
        <Grid container mt={2} spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
                <Autocomplete
                    options={verificationAgencies}
                    value={verificationAgency}
                    id="verificationAgencyAutoComplete"
                    getOptionLabel={(option) => option.vaName.toString()}
                    isOptionEqualToValue={(option, value) => option.vaId === value.vaId}
                    disableClearable
                    onChange={(_, data) => data && setVerificationAgency(data)}
                    renderInput={(params) => (
                        <TextField
                            label="Select Verification Agency"
                            variant="outlined"
                            {...params}
                            id="verificationAgency"
                            fullWidth
                            size="medium"
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                {scorecardsLoading ? (
                    <SkeletonLoading formLoading={true} />
                ) : (
                    <AutocompleteWithInfo
                        key="scorecardAutoComplete"
                        control={control}
                        name="scorecard"
                        label="Select Scorecard"
                        items={scorecards}
                        size="medium"
                        useManualFiltering={false}
                        style={{ width: "100%" }}
                        disabled={scorecards.length === 0}
                        onInputFieldChange={handleScorecardChange}
                    />
                )}
            </Grid>
        </Grid>
    );

    return (
        <Grid item xs={12} xl={6}>
            <MDBox mb={3}>
                <MDTypography variant="h4" fontWeight="bold" gutterBottom>
                    VA Excel Exports
                </MDTypography>
            </MDBox>
            {isLoading ? (
                <IntermediateGlobalLoading />
            ) : (
                <>
                    <Card elevation={3}>
                        <CardContent sx={{ px: 3, py: 2 }}>
                            <MDTypography variant="h5" gutterBottom>
                                Select a Verification Agency and Scorecard
                            </MDTypography>
                            <MDTypography variant='body2'>
                                Available options are based on currently supported Verification Agency templates and their associated scorecards in BEE123. <br></br>
                                More options will be added as additional VA templates become available.
                            </MDTypography>
                            {RenderDropdowns()}
                            <Box mt={3} display="flex" justifyContent="flex-end">
                                <LoadingButton
                                    loading={isButtonLoading}
                                    disabled={!verificationAgency || !selectedScorecard}
                                    variant="contained"
                                    color="primary"
                                    onClick={exportVAPack}
                                >
                                    Export Pack
                                </LoadingButton>
                            </Box>
                        </CardContent>
                    </Card>
                    {(isProcessingFile) && (
                        <MDBox mt={3} >
                            <Grid container >
                                <Grid item xs={12} lg={12}>
                                    <ProgressBar
                                        animated
                                        now={progress}
                                        variant="success"
                                        style={{ transition: "width 0.5s ease-in-out" }}
                                    />
                                </Grid>
                            </Grid>
                        </MDBox>
                    )}
                </>
            )}
            <MDBox mt={4}>
                <MDTypography variant="h4" fontWeight="bold" gutterBottom>
                    Exports History
                </MDTypography>
                <VAExcelExportsHistoryTable key={tableRefreshKey} />
            </MDBox>
        </Grid>
    );
};